import { Quote, CheckCircle2 } from "lucide-react";
import { useReveal } from "../hooks/useReveal";

const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d4897236-428b-4abf-b3c0-e7679c5a4973/images/0ffe32594ab8b5d6d79dcd6d9d039871144f0312639caa02cf0931543c2355c8.png";

const PILLARS = [
  "Relationship-based care, not transactional visits",
  "Dignity-first approach for every individual",
  "Consistent caregivers your family can trust",
  "Personalized care plans — never templates",
];

export default function About() {
  const [ref, visible] = useReveal();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32 bg-[#F8F9FA]/40 overflow-hidden"
    >
      <div className="absolute inset-0 grain" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Image stack */}
        <div className="lg:col-span-5 relative">
          <div className="relative aspect-[4/5] rounded-3xl overflow-hidden shadow-[0_30px_60px_-20px_rgba(15,23,42,0.25)]">
            <img
              src={ABOUT_IMG}
              alt="Caregiver and senior at home, sharing a warm, dignified moment"
              className="w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#005165]/30 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-2 sm:-right-6 bg-white rounded-2xl p-5 sm:p-6 shadow-xl border border-[#E5DDC9] max-w-[280px] float-y">
            <Quote className="w-5 h-5 text-[#AC9667] mb-2" />
            <p className="text-[14px] text-[#1F2937] italic leading-snug">
              "We treat every family the way we'd want our own to be cared for."
            </p>
          </div>
          <div className="hidden md:block absolute -top-4 -left-4 w-24 h-24 rounded-full border-2 border-[#AC9667]/40" />
        </div>

        {/* Content */}
        <div
          ref={ref}
          className={`lg:col-span-7 ${visible ? "reveal visible" : "opacity-0"}`}
        >
          <span className="gold-divider mb-5">Our Story</span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-[#005165] leading-[1.08]">
            Care that feels like family.
            <span className="block text-[#AC9667] italic font-light">Trust that feels like home.</span>
          </h2>
          <p className="mt-6 text-[17px] md:text-lg text-[#4B5563] leading-relaxed">
            Madison Peaceful Path Home Care was built on a quiet, powerful belief: seniors deserve
            to age with respect, joy, and independence — and families deserve real peace of mind.
            We aren't a clinical agency that rotates faces through your door. We are neighbors,
            committed to consistency, communication, and compassion.
          </p>
          <p className="mt-5 text-[17px] text-[#4B5563] leading-relaxed">
            Every plan we create is grounded in relationships — caregivers who listen, learn your
            loved one's preferences, and show up the same way every time. Because at home,
            consistency is comfort. And comfort is everything.
          </p>

          <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {PILLARS.map((p) => (
              <li
                key={p}
                data-testid={`about-pillar-${p.slice(0, 15).replace(/\s/g, "-")}`}
                className="flex items-start gap-3 text-[15px] text-[#005165]"
              >
                <CheckCircle2 className="w-5 h-5 text-[#AC9667] shrink-0 mt-0.5" strokeWidth={1.6} />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
